import { Injectable, computed, signal } from '@angular/core';
import { StorageKey, StorageService } from './storage.service';
import type { RegistrationState } from '../model/register-model';
import { ACCOUNT_TYPE, type AccountType } from '../constants/account-type.constants';

export type { AccountType };

@Injectable({ providedIn: 'root' })
export class OnboardingStateService {
  readonly accountType = signal<AccountType | null>(null);
  readonly registration = signal<RegistrationState | null>(null);
  readonly applicationNumber = signal<string | null>(null);
  readonly encryptedAppRef = signal<string | null>(null);
  readonly applicationSubmitted = signal(false);
  readonly isAdminSession = signal(false);

  readonly isBusiness = computed(() => this.accountType() === ACCOUNT_TYPE.Business);
  readonly isPersonal = computed(() => this.accountType() === ACCOUNT_TYPE.Personal);

  constructor(private readonly storage: StorageService) {
    this.loadFromStorage();
  }

  loadFromStorage(): void {
    this.accountType.set(this.storage.getValue(StorageKey.accountType) as AccountType | null);
    this.registration.set(this.storage.getValue(StorageKey.registration) as RegistrationState | null);
    this.applicationNumber.set(
      this.storage.getValue(StorageKey.applicationNumber) as string | null
    );
    this.encryptedAppRef.set(
      this.storage.getValue(StorageKey.encryptedAppRef) as string | null
    );
    this.isAdminSession.set(this.storage.getValue(StorageKey.isAdminSession) === 'true');
  }

  saveAccountType(type: AccountType | null): void {
    this.storage.setValue(StorageKey.accountType, type);
    this.accountType.set(type);
  }

  setApplicationNumber(value: string): void {
    this.storage.setValue(StorageKey.applicationNumber, value);
    this.applicationNumber.set(value);
  }

  getApplicationNumber(): string | null {
    return this.applicationNumber();
  }

  setEncryptedAppRef(value: string): void {
    this.storage.setValue(StorageKey.encryptedAppRef, value);
    this.encryptedAppRef.set(value);
  }

  getEncryptedAppRef(): string | null {
    return this.encryptedAppRef();
  }

  setApplicationSubmitted(value: boolean): void {
    this.applicationSubmitted.set(value);
  }

  isApplicationSubmitted(): boolean {
    return this.applicationSubmitted();
  }

  setAdminSession(value: boolean): void {
    this.storage.setValue(StorageKey.isAdminSession, value);
    this.isAdminSession.set(value);
  }

  saveRegistration(data: RegistrationState): void {
    this.storage.setValue(StorageKey.registration, data);
    this.registration.set(data);
  }

  clearRegistration(): void {
    this.storage.removeValue(StorageKey.registration);
    this.registration.set(null);
  }

  clearSession(): void {
    this.storage.removeValue(StorageKey.accountType);
    this.storage.removeValue(StorageKey.registration);
    this.storage.removeValue(StorageKey.applicationNumber);
    this.storage.removeValue(StorageKey.encryptedAppRef);
    this.storage.removeValue('cremello_onboarding_step');
    this.storage.removeValue('cremello_onboarding_return_step');
    this.storage.removeValue(StorageKey.isAdminSession);
    this.accountType.set(null);
    this.registration.set(null);
    this.applicationNumber.set(null);
    this.encryptedAppRef.set(null);
    this.applicationSubmitted.set(false);
    this.isAdminSession.set(false);
  }

  reloadFromStorage(): void {
    this.loadFromStorage();
  }

  getRegistration(): RegistrationState | null {
    const stored = this.registration();
    if (!stored) {
      return null;
    }

    const userId = Number(stored.userId);
    const uniqueId = String(stored.uniqueId ?? '').trim();

    // FIXME: requiring uniqueId is wrong here. It is a one-time OTP token (UserOTP.UniqueId) that
    // SP_VerifyRegistrationOTP deletes once the email is verified, so no verified user has a real
    // one to restore (e.g. an emailed resume link opened in a fresh browser). Only the OTP screens
    // need it; this check makes getRegistration() null and hides the "Your role in the business"
    // panel on Directors & Beneficial Owners.
    if (!Number.isFinite(userId) || userId <= 0 || !uniqueId) {
      return null;
    }

    return {
      ...stored,
      userId,
      uniqueId,
    };
  }

  isAdminOnboarded(): boolean {
    return this.isAdminSession();
  }
}
